import { FC, useEffect, useRef, useState } from 'react'
import { LayoutAnimation, Platform, UIManager } from 'react-native'

import { publishEvent } from '@zet/analytics'
import { useGenerateDownloadReportCSV } from '@zet/common/api/hooks/useGenerateDownloadReportCSV'
import { useToast } from '@zet/common/components/toast'
import { useDownloadFile } from '@zet/common/hooks/useDownloadFile'
import { useModal } from '@zet/common/hooks/useModal'
import { MY_TEAMS_ADVISOR } from '@zet/constants'
import { reportDownloadOptions } from '@zet/constants/leadReport'
import { B2, BottomSheet, Button, SH1, Stack, XStack } from '@zet/design-system'
import { Select } from '@zet/design-system/select'
import { APP_NAME } from '@zet/end-points'
import { useLeadsReport } from '@zet/feature-leads/api/leadReport/hooks'
import { useUserContext } from '@zet/feature-user'
import { i18n } from '@zet/localization'
import { navigationRef } from '@zet/utils/navigation'
import moment from 'moment'

if (Platform.OS === 'android') {
  if (UIManager.setLayoutAnimationEnabledExperimental) {
    UIManager.setLayoutAnimationEnabledExperimental(true)
  }
}

interface LeadReportDownloadProps {
  isVisible: boolean
  hideBottomSheet: VoidFunction
  isMyTeamsReq?: boolean
  sheetName?: string
}

const LeadReportDownload: FC<LeadReportDownloadProps> = ({
  isVisible,
  hideBottomSheet,
  isMyTeamsReq = false,
  sheetName,
}) => {
  const toast = useToast()
  const { openModal } = useModal()

  const isDownloadPressed = useRef(false)

  const [selectedFromDate, setSelectedFromDate] = useState('')
  const [isDownloading, setIsDownloading] = useState(false)
  const [isDownloadTakingTooLong, setDownloadTakingTooLong] = useState(false)
  const { data: user } = useUserContext()
  const { mutate: generateReport, isLoading: generateReportLoading } =
    useGenerateDownloadReportCSV()
  const { data, isLoading, refetch, isFetched } = useLeadsReport(
    { from: selectedFromDate, to: moment().format('YYYY-MM-DD') },
    isMyTeamsReq
  )

  const onDownload = async () => {
    if (APP_NAME !== MY_TEAMS_ADVISOR) {
      import('@zet/common/utils/requestNotificationPermission').then(
        async ({ requestNotificationPermission }) => {
          const isNotificationPermissionAllowed =
            await requestNotificationPermission()
          if (isNotificationPermissionAllowed) {
            generateReport(
              {
                start_date: selectedFromDate,
                end_date: moment().format('YYYY-MM-DD'),
                report_type: 'LEADS',
                is_my_teams_req: isMyTeamsReq,
              },
              {
                onSuccess: async (response) => {
                  console.log('generateReport onSuccess: ', response)
                },
                onError: () => {
                  toast?.show({
                    intent: 'error',
                    heading: ' Oops, Something Went Wrong',
                    description: 'An error occurred. Please try again later.',
                    hideAfter: 3000,
                  })
                },
              }
            )
            toast?.show({
              intent: 'success',
              heading: 'Preparing your report',
              description: 'We’re preparing your earnings report.',
              hideAfter: 3000,
            })
            hideBottomSheet()
          } else {
            toast?.show({
              intent: 'error',
              heading: 'Permission Denied',
              hideAfter: 3000,
            })
          }
        }
      )
    }
  }

  const onSuccess = () => {
    hideBottomSheet()
    toast?.show({
      intent: 'success',
      heading: i18n.t('success'),
      description: i18n.t('toastMessages.downloadSuccessful'),
      hideAfter: 3000,
    })
  }
  const { downloadFile } = useDownloadFile({
    onSuccess,
    onProgress: () => setIsDownloading(true),
    onError: () => {
      isDownloadPressed.current = false
      setIsDownloading(false)
    },
  })

  useEffect(() => {
    if (isDownloadPressed?.current && isFetched) {
      if (data?.SignedUrl) {
        if (Platform.OS === 'web') {
          window.open(data?.SignedUrl, '_blank')
        } else {
          downloadFile(data?.SignedUrl || '')
        }
      } else {
        openModal('NoRequestedDataFoundModal', {
          message: i18n.t('noDataForSelected'),
        })
      }
      isDownloadPressed.current = false
      setIsDownloading(false)
      hideBottomSheet()
    }
  }, [isDownloadPressed?.current, isFetched])

  const onChangeSelect = (value: string) => {
    navigationRef.current?.getCurrentRoute()?.name === 'MyTeams' &&
      publishEvent('click_action', {
        item_name: 'my_teams_range_selection',
      })

    setSelectedFromDate(value)
  }

  const onPressDownload = () => {
    if (APP_NAME === MY_TEAMS_ADVISOR) {
      refetch()
      if (user?.email) {
        setTimeout(() => {
          LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut)
          setDownloadTakingTooLong(true)
        }, 30000)
      }

      isDownloadPressed.current = true
      setIsDownloading(true)
    } else {
      onDownload()
    }
  }

  return (
    <BottomSheet
      visible={isVisible}
      disableBottomSheet={hideBottomSheet}
      onDismiss={hideBottomSheet}
      sheetName={sheetName}
    >
      <>
        <Stack className="px-3 py-3">
          <B2>{i18n.t('myLeads.leadReportSelectTitle')}</B2>
          {isDownloadTakingTooLong && (
            <B2 className="text-warning-100">
              {i18n.t('leads.reportDownloadTakingTooLong')}
            </B2>
          )}
          <Stack className="mt-3">
            <Select onChange={onChangeSelect}>
              {reportDownloadOptions.map((op, i) => (
                <Select.Option
                  key={op?.value}
                  showToggle={false}
                  name={op?.value}
                  selectedValue={selectedFromDate}
                  optionContainerStyle="h-[62] mb-3 w-full justify-center rounded-lg"
                >
                  <SH1>{op?.label}</SH1>
                </Select.Option>
              ))}
            </Select>
          </Stack>
        </Stack>
        <Stack className="border-grays-30 border-t px-3 py-3">
          <XStack className="justify-between">
            <Button
              className="bg-grays-20 mr-2 flex-grow"
              size={'lg'}
              textStyle="text-grays-80"
              onPress={() => {
                hideBottomSheet()
              }}
              eventName="click_action"
              eventPayload={{
                item_name:
                  navigationRef.current?.getCurrentRoute()?.name === 'MyTeams'
                    ? 'my_teams_report_download_cancel'
                    : 'my_leads_cancel_report',
              }}
            >
              {i18n.t('buttonGroup.cancel')}
            </Button>
            <Button
              disabled={!selectedFromDate}
              className="ml-2 flex-grow"
              size={'lg'}
              onPress={onPressDownload}
              loading={
                isDownloadPressed?.current &&
                (isDownloading || isLoading || generateReportLoading)
              }
              eventName="click_action"
              eventPayload={{
                item_name:
                  navigationRef.current?.getCurrentRoute()?.name === 'MyTeams'
                    ? 'my_teams_download_file'
                    : 'my_leads_download_file',
                user_id: user?.id,
                date_selected: selectedFromDate,
              }}
            >
              {i18n.t('buttonGroup.download')}
            </Button>
          </XStack>
        </Stack>
      </>
    </BottomSheet>
  )
}

export default LeadReportDownload
